import React, { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Typography,
  Space,
  Button,
  Tabs,
  Form,
  InputNumber,
  Input,
  Select,
  message,
  Statistic,
  Row,
  Col,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ProjectSelector } from '@/components/project/ProjectSelector';
import { ItemSelect } from '@/components/inventory/ItemSelect';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import {
  getCriticalItemRowClassName,
  ItemCodeWithCritical,
} from '@/components/inventory/criticalItemUi';
import { useCriticalItemLookup } from '@/hooks/useCriticalItemLookup';
import { useWarehouses } from '@/hooks/useInventory';
import siteMaterialService, { type SiteStockRow } from '@/services/siteMaterial.service';
import { useCurrency } from '@/contexts/CurrencyContext';
import { useAuthStore } from '@/store/authStore';
import { isPlatformSuperAdmin } from '@/utils/authUser';

const { Title, Text } = Typography;

const ProjectSiteMaterialsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get('projectId') || '';
  const [warehouseFilter, setWarehouseFilter] = useState<string>('');
  const { formatCurrency } = useCurrency();
  const queryClient = useQueryClient();
  const { isCriticalCode } = useCriticalItemLookup();
  const user = useAuthStore((s) => s.user);
  const canConsume =
    isPlatformSuperAdmin(user) ||
    user?.permissions?.includes('inventory.material_issue.create');
  const canFoc =
    isPlatformSuperAdmin(user) ||
    user?.permissions?.includes('inventory.adjustment.create') ||
    user?.permissions?.includes('inventory.material_issue.create');

  const { data: warehouses = [] } = useWarehouses();
  const projectWipWarehouses = useMemo(
    () => warehouses.filter((w) => w.project_id && (!projectId || w.project_id === projectId)),
    [warehouses, projectId]
  );

  const { data: stock, isLoading } = useQuery({
    queryKey: ['site-stock', projectId, warehouseFilter],
    queryFn: () =>
      siteMaterialService.getSiteStock(projectId, {
        warehouse_id: warehouseFilter || undefined,
      }),
    enabled: !!projectId,
  });

  const [consumeForm] = Form.useForm();
  const [focForm] = Form.useForm();
  const focSupplyKind = Form.useWatch('supply_kind', focForm);

  const consumeMutation = useMutation({
    mutationFn: siteMaterialService.consume.bind(siteMaterialService),
    onSuccess: () => {
      message.success('Site consumption posted (MR + MI + project cost)');
      queryClient.invalidateQueries({ queryKey: ['site-stock'] });
      consumeForm.resetFields(['lines', 'notes']);
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      message.error(e.response?.data?.message || 'Consumption failed');
    },
  });

  const focMutation = useMutation({
    mutationFn: siteMaterialService.focReceipt.bind(siteMaterialService),
    onSuccess: () => {
      message.success('FOC / owner-supplied receipt posted');
      queryClient.invalidateQueries({ queryKey: ['site-stock'] });
      focForm.resetFields(['lines', 'notes', 'reference_id']);
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      message.error(e.response?.data?.message || 'FOC receipt failed');
    },
  });

  const columns: ColumnsType<SiteStockRow> = [
    { title: 'Warehouse', dataIndex: 'warehouse_name', width: 160, ellipsis: true },
    {
      title: 'Item',
      dataIndex: 'item_code',
      width: 140,
      render: (code: string, r) => (
        <ItemCodeWithCritical code={code} critical={isCriticalCode(r.item_code)} />
      ),
    },
    { title: 'Description', dataIndex: 'item_name', ellipsis: true },
    { title: 'Heat', dataIndex: 'heat_number', width: 100 },
    { title: 'Dim', dataIndex: 'dimension', width: 90 },
    {
      title: 'Qty',
      dataIndex: 'available_qty',
      width: 100,
      align: 'right',
      render: (v: number, r) => `${Number(v).toFixed(3)} ${r.uom || ''}`,
    },
    {
      title: 'Avg cost',
      dataIndex: 'average_cost',
      width: 110,
      align: 'right',
      render: (v: number) => formatCurrency(Number(v) || 0),
    },
    {
      title: 'Value',
      dataIndex: 'total_value',
      width: 120,
      align: 'right',
      render: (v: number) => formatCurrency(Number(v) || 0),
    },
  ];

  const totalValue = (stock?.rows || []).reduce((s, r) => s + (Number(r.total_value) || 0), 0);
  const totalQty = (stock?.rows || []).reduce((s, r) => s + (Number(r.available_qty) || 0), 0);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Title level={3} style={{ marginBottom: 4 }}>
            Project site materials
          </Title>
          <Text type="secondary">
            On-hand in project WIP warehouses, site consumption, and owner-supplied / FOC receipts.
          </Text>
        </div>

        <Card size="small">
          <Space wrap>
            <span>Project</span>
            <ProjectSelector
              value={projectId || undefined}
              onChange={(id) => {
                const next = new URLSearchParams(searchParams);
                if (id) next.set('projectId', id);
                else next.delete('projectId');
                setSearchParams(next);
                setWarehouseFilter('');
              }}
              allowClear
            />
            <Select
              allowClear
              placeholder="All site warehouses"
              style={{ minWidth: 220 }}
              value={warehouseFilter || undefined}
              onChange={(v) => setWarehouseFilter(v || '')}
              options={projectWipWarehouses.map((w) => ({
                value: w.id,
                label: w.warehouse_name || w.warehouse_code,
              }))}
              disabled={!projectId}
            />
          </Space>
        </Card>

        {projectId ? (
          <Row gutter={16}>
            <Col>
              <Statistic title="Lines on hand" value={stock?.rows?.length || 0} />
            </Col>
            <Col>
              <Statistic title="Total qty" value={totalQty} precision={2} />
            </Col>
            <Col>
              <Statistic title="Total value" value={totalValue} formatter={(v) => formatCurrency(Number(v))} />
            </Col>
          </Row>
        ) : null}

        <Tabs
          items={[
            {
              key: 'stock',
              label: 'Site stock',
              children: (
                <Table
                  rowKey={(r) =>
                    `${r.warehouse_id}-${r.item_id}-${r.heat_number}-${r.dimension}`
                  }
                  loading={isLoading}
                  columns={columns}
                  dataSource={stock?.rows || []}
                  pagination={{ pageSize: 50 }}
                  scroll={{ x: 1000 }}
                  rowClassName={(r) =>
                    getCriticalItemRowClassName({ is_critical: isCriticalCode(r.item_code) })
                  }
                  locale={{
                    emptyText: projectId
                      ? 'No on-hand qty in project WIP warehouses'
                      : 'Select a project',
                  }}
                />
              ),
            },
            {
              key: 'consume',
              label: 'Site consumption',
              disabled: !canConsume || !projectId,
              children: (
                <Card size="small" title="Consume from project WIP">
                  <Form
                    form={consumeForm}
                    layout="vertical"
                    onFinish={(values) => {
                      consumeMutation.mutate({
                        project_id: projectId,
                        warehouse_id: values.warehouse_id,
                        department_id: values.department_id,
                        notes: values.notes,
                        lines: (values.lines || []).map(
                          (l: {
                            item_id: string;
                            quantity: number;
                            uom?: string;
                            heat_number?: string;
                            dimension?: string;
                          }) => ({
                            item_id: l.item_id,
                            quantity: Number(l.quantity),
                            uom: l.uom,
                            heat_number: l.heat_number,
                            dimension: l.dimension,
                          })
                        ),
                      });
                    }}
                  >
                    <Form.Item
                      name="warehouse_id"
                      label="Site WIP warehouse"
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={projectWipWarehouses.map((w) => ({
                          value: w.id,
                          label: w.warehouse_name || w.warehouse_code,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      name="department_id"
                      label="Department"
                      rules={[{ required: true, message: 'Department is required' }]}
                    >
                      <DepartmentSelect style={{ width: '100%', maxWidth: 400 }} />
                    </Form.Item>
                    <Form.List name="lines" initialValue={[{}]}>
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field) => (
                            <Space key={field.key} align="start" wrap style={{ display: 'flex', marginBottom: 8 }}>
                              <Form.Item
                                {...field}
                                name={[field.name, 'item_id']}
                                rules={[{ required: true, message: 'Item' }]}
                              >
                                <ItemSelect style={{ width: 240 }} />
                              </Form.Item>
                              <Form.Item
                                {...field}
                                name={[field.name, 'quantity']}
                                rules={[{ required: true }]}
                              >
                                <InputNumber min={0.0001} placeholder="Qty" />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, 'heat_number']}>
                                <Input placeholder="Heat" style={{ width: 100 }} />
                              </Form.Item>
                              <Form.Item {...field} name={[field.name, 'dimension']}>
                                <Input placeholder="Dim" style={{ width: 100 }} />
                              </Form.Item>
                              <Button type="link" danger onClick={() => remove(field.name)}>
                                Remove
                              </Button>
                            </Space>
                          ))}
                          <Button type="dashed" onClick={() => add()} style={{ marginBottom: 12 }}>
                            Add line
                          </Button>
                        </>
                      )}
                    </Form.List>
                    <Form.Item name="notes" label="Notes">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={consumeMutation.isPending}>
                      Post consumption
                    </Button>
                  </Form>
                </Card>
              ),
            },
            {
              key: 'foc',
              label: 'FOC / owner-supplied',
              disabled: !canFoc || !projectId,
              children: (
                <Card size="small" title="Receive owner-supplied or subcon free-issue into WIP (zero cost)">
                  <Form
                    form={focForm}
                    layout="vertical"
                    initialValues={{ supply_kind: 'OwnerSupplied' }}
                    onFinish={(values) => {
                      focMutation.mutate({
                        project_id: projectId,
                        warehouse_id: values.warehouse_id,
                        supply_kind: values.supply_kind,
                        reference_id: values.reference_id,
                        notes: values.notes,
                        lines: (values.lines || []).map(
                          (l: {
                            item_id: string;
                            quantity: number;
                            uom?: string;
                            heat_number?: string;
                            dimension?: string;
                          }) => ({
                            item_id: l.item_id,
                            quantity: Number(l.quantity),
                            uom: l.uom,
                            heat_number: l.heat_number,
                            dimension: l.dimension,
                          })
                        ),
                      });
                    }}
                  >
                    <Form.Item name="supply_kind" label="Supply kind" rules={[{ required: true }]}>
                      <Select
                        options={[
                          { value: 'OwnerSupplied', label: 'Owner-supplied / FOC' },
                          { value: 'SubconFreeIssue', label: 'Subcon free-issue' },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item
                      name="warehouse_id"
                      label="Site WIP warehouse"
                      rules={[{ required: true }]}
                    >
                      <Select
                        options={projectWipWarehouses.map((w) => ({
                          value: w.id,
                          label: w.warehouse_name || w.warehouse_code,
                        }))}
                      />
                    </Form.Item>
                    <Form.Item
                      name="reference_id"
                      label={
                        focSupplyKind === 'SubconFreeIssue'
                          ? 'Subcontractor agreement (SCA) id'
                          : 'Reference'
                      }
                      rules={
                        focSupplyKind === 'SubconFreeIssue'
                          ? [{ required: true, message: 'SCA id required for subcon free-issue' }]
                          : undefined
                      }
                      extra={
                        focSupplyKind === 'SubconFreeIssue'
                          ? 'Links FOC to SCA (reference_type=SubcontractorAgreement) for SPC advisory evidence'
                          : 'Optional'
                      }
                    >
                      <Input
                        placeholder={
                          focSupplyKind === 'SubconFreeIssue' ? 'SCA uuid (required)' : 'Optional'
                        }
                      />
                    </Form.Item>
                    <Form.List name="lines" initialValue={[{}]}>
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map((field) => (
                            <Space key={field.key} align="start" wrap style={{ display: 'flex', marginBottom: 8 }}>
                              <Form.Item
                                {...field}
                                name={[field.name, 'item_id']}
                                rules={[{ required: true }]}
                              >
                                <ItemSelect style={{ width: 240 }} />
                              </Form.Item>
                              <Form.Item
                                {...field}
                                name={[field.name, 'quantity']}
                                rules={[{ required: true }]}
                              >
                                <InputNumber min={0.0001} placeholder="Qty" />
                              </Form.Item>
                              <Button type="link" danger onClick={() => remove(field.name)}>
                                Remove
                              </Button>
                            </Space>
                          ))}
                          <Button type="dashed" onClick={() => add()} style={{ marginBottom: 12 }}>
                            Add line
                          </Button>
                        </>
                      )}
                    </Form.List>
                    <Form.Item name="notes" label="Notes">
                      <Input.TextArea rows={2} />
                    </Form.Item>
                    <Button type="primary" htmlType="submit" loading={focMutation.isPending}>
                      Post FOC receipt
                    </Button>
                  </Form>
                </Card>
              ),
            },
          ]}
        />
      </Space>
    </div>
  );
};

export default ProjectSiteMaterialsPage;
